import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { BarChart, BarChartLegende } from "@/components/BarChart"
import { Input, Select } from "@/components/ui"
import {
  EREIGNISTYP_LABEL,
  fetchEreignisJeArbeitsort,
  fetchEreignisVergleich,
  type EreignisDashboardFilter,
} from "@/lib/api"

const MAX_ZEITRAEUME = 4
const FARBEN = ["#0f172a", "#2563eb", "#16a34a", "#ea580c"]

interface ZeitraumFormular {
  von: string
  bis: string
}

const LEERER_ZEITRAUM: ZeitraumFormular = { von: "", bis: "" }

const RICHTUNG_LABEL: Record<string, string> = {
  gestiegen: "gestiegen ↑",
  gesunken: "gesunken ↓",
  gleich: "unverändert",
  neu: "neu (vorher 0)",
  keine_daten: "keine Daten",
}

/**
 * Ereignis-Dashboard (AP-4.5, 10-F6) — bis zu 4 Vergleichszeiträume.
 *
 * Chart-Rendering LOKAL gebündelt (`BarChart`, kein CDN — ersetzt die
 * Ninox-ECharts-CDN-Injection R-E-04/R-E-07). Filter-Inkonsistenzen aus der
 * Ninox-Analyse (R-E-08: Zeitraum 1/2 wandten Ereignistyp-/Arbeitsort-Filter
 * NICHT an, Zeitraum 3/4 schon) sind bereinigt: EIN einheitlicher Filtersatz
 * gilt für alle Zeiträume (Backend `EreignisFilter`, Bugfix dokumentiert in
 * `ereignis_dashboard_service.py`).
 *
 * Impersonation (10-F7, R-E-11 "Dashboard als anderer Mitarbeiter ansehen")
 * ist bewusst NICHT umgesetzt: ohne Pro-Nutzer-Dashboard-Datensätze
 * (ARCHITEKTUR.md E4) gibt es keinen Row-Level-Gegenstand mehr, den eine
 * Impersonation freischalten müsste — jede berechtigte Person sieht
 * dieselbe Live-Aggregation. Ein Audit-Log für eine Funktion ohne
 * Datenschutzwirkung wäre reine Formsache ohne fachlichen Nutzen.
 */
export function EreignisDashboardPage() {
  const [anzahlZeitraeume, setAnzahlZeitraeume] = useState(2)
  const [zeitraeume, setZeitraeume] = useState<ZeitraumFormular[]>([
    { ...LEERER_ZEITRAUM },
    { ...LEERER_ZEITRAUM },
  ])
  const [ereignistyp, setEreignistyp] = useState("")
  const [arbeitsortVon, setArbeitsortVon] = useState("")
  const [arbeitsortBis, setArbeitsortBis] = useState("")

  const filterListe: EreignisDashboardFilter[] = zeitraeume.slice(0, anzahlZeitraeume).map((z) => ({
    von: z.von || undefined,
    bis: z.bis || undefined,
    ereignistyp: (ereignistyp || undefined) as EreignisDashboardFilter["ereignistyp"],
  }))

  const { data: vergleich, isLoading, isError } = useQuery({
    queryKey: ["ereignis-vergleich", filterListe],
    queryFn: () => fetchEreignisVergleich(filterListe),
  })

  const { data: jeArbeitsort } = useQuery({
    queryKey: ["ereignis-je-arbeitsort", arbeitsortVon, arbeitsortBis],
    queryFn: () =>
      fetchEreignisJeArbeitsort({
        von: arbeitsortVon || undefined,
        bis: arbeitsortBis || undefined,
      }),
  })

  const kategorien = Object.keys(EREIGNISTYP_LABEL)
  const serien = (vergleich?.zeitraeume ?? []).map((z, i) => ({
    label: `Zeitraum ${i + 1}`,
    farbe: FARBEN[i % FARBEN.length],
    werte: kategorien.map((k) => z.ereignisseJeTyp[k] ?? 0),
  }))

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ereignis-Dashboard</h1>
          <p className="mt-1 text-sm text-slate-600">
            Bis zu {MAX_ZEITRAEUME} Vergleichszeiträume (10-F6) — Diagramme lokal gerendert, kein CDN.
          </p>
        </div>
        <Link to="/ereignis" className="text-sm text-slate-600 hover:underline">
          ← zur Ereignisliste
        </Link>
      </header>

      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Anzahl Zeiträume</label>
          <Select
            value={anzahlZeitraeume}
            onChange={(e) => {
              const n = Number(e.target.value)
              setAnzahlZeitraeume(n)
              setZeitraeume((z) => {
                const kopie = [...z]
                while (kopie.length < n) kopie.push({ ...LEERER_ZEITRAUM })
                return kopie
              })
            }}
            className="h-8 w-auto"
          >
            {[1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>

          <label className="text-sm font-medium text-slate-700">Ereignistyp</label>
          <Select
            value={ereignistyp}
            onChange={(e) => setEreignistyp(e.target.value)}
            className="h-8 w-auto"
          >
            <option value="">Alle</option>
            {Object.entries(EREIGNISTYP_LABEL).map(([wert, label]) => (
              <option key={wert} value={wert}>
                {label}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {zeitraeume.slice(0, anzahlZeitraeume).map((z, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <p className="mb-2 text-xs font-semibold" style={{ color: FARBEN[i % FARBEN.length] }}>
                Zeitraum {i + 1}
              </p>
              <label className="mb-1 block text-xs text-slate-500">Von</label>
              <Input
                type="date"
                value={z.von}
                onChange={(e) =>
                  setZeitraeume((prev) =>
                    prev.map((p, idx) => (idx === i ? { ...p, von: e.target.value } : p)),
                  )
                }
                className="mb-2 h-8 text-xs"
              />
              <label className="mb-1 block text-xs text-slate-500">Bis</label>
              <Input
                type="date"
                value={z.bis}
                onChange={(e) =>
                  setZeitraeume((prev) =>
                    prev.map((p, idx) => (idx === i ? { ...p, bis: e.target.value } : p)),
                  )
                }
                className="h-8 text-xs"
              />
            </div>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-sm text-slate-500">Lade…</p>}
      {isError && <p className="text-sm text-red-700">Auswertung nicht verfügbar.</p>}

      {vergleich && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-900">
            Vergleich Ereignisart (R-E-04-Äquivalent)
          </h2>
          <BarChart kategorien={kategorien.map((k) => EREIGNISTYP_LABEL[k as never])} serien={serien} />
          <BarChartLegende serien={serien} />

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {vergleich.vergleichZeitraumEinsZwei && (
              <VergleichsKarte
                titel="Zeitraum 1 → 2"
                vergleich={vergleich.vergleichZeitraumEinsZwei}
              />
            )}
            {vergleich.vergleichZeitraumDreiVier && (
              <VergleichsKarte
                titel="Zeitraum 3 → 4"
                vergleich={vergleich.vergleichZeitraumDreiVier}
              />
            )}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-900">
          Vergleich Arbeitsort (R-E-07-Äquivalent)
        </h2>
        <div className="mb-3 flex gap-2">
          <Input
            type="date"
            value={arbeitsortVon}
            onChange={(e) => setArbeitsortVon(e.target.value)}
            className="h-8 w-auto text-xs"
          />
          <Input
            type="date"
            value={arbeitsortBis}
            onChange={(e) => setArbeitsortBis(e.target.value)}
            className="h-8 w-auto text-xs"
          />
        </div>
        {jeArbeitsort && jeArbeitsort.length > 0 ? (
          <BarChart
            kategorien={jeArbeitsort.map((a) => a.anlageBezeichnung)}
            serien={[{ label: "Ereignisse", farbe: "#0f172a", werte: jeArbeitsort.map((a) => a.anzahl) }]}
          />
        ) : (
          <p className="text-sm text-slate-400">Keine Daten für den gewählten Zeitraum.</p>
        )}
      </div>
    </div>
  )
}

function VergleichsKarte({
  titel,
  vergleich,
}: {
  titel: string
  vergleich: { zahl1: number; zahl2: number; prozent: number | null; richtung: string }
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm">
      <p className="font-semibold text-slate-800">{titel}</p>
      <p className="text-slate-600">
        {vergleich.zahl1} → {vergleich.zahl2} Ereignisse
      </p>
      <p className="text-slate-500">
        {RICHTUNG_LABEL[vergleich.richtung] ?? vergleich.richtung}
        {vergleich.prozent !== null && ` (${vergleich.prozent}%)`}
      </p>
    </div>
  )
}
